<template>
    <div class="top-bar" tabindex="-1">
        <div class="top-bar-logo-td">
            <button type="button" class="top-bar-button top-bar-menu-button" :title="t('Main menu')" @click="clickMenu">
                <i class="fas fa-bars"></i>
            </button>
            <span class="top-bar-title">{{ platformName }}</span>
            <span class="top-bar-title-min">{{ platformName }}</span>
        </div>

        <div class="top-bar-user-td">
            <template v-if="loggedIn">
                <button
                    type="button"
                    class="top-bar-button top-bar-profile-button"
                    :title="t('Account settings')"
                    @click="openAccountSettings"
                >
                    <i class="fas fa-user"></i>
                </button>
                <button
                    type="button"
                    class="top-bar-button top-bar-notifications-button"
                    :class="{ 'has-notifications': hasNotifications }"
                    :title="t('Notifications')"
                    @click="openNotifications"
                >
                    <i class="fas fa-bell"></i>
                </button>
                <button type="button" class="top-bar-button top-bar-settings-button" :title="t('Settings')" @click="openUserSettings">
                    <i class="fas fa-cog"></i>
                </button>
            </template>
            <template v-else>
                <button type="button" class="top-bar-button" :title="t('Select your language')" @click="selectLanguage">
                    <i class="fas fa-language"></i>
                </button>
                <button type="button" class="top-bar-button" :title="t('Change theme')" @click="invertTheme">
                    <i v-if="theme === 'dark'" class="fas fa-sun"></i>
                    <i v-else class="fas fa-moon"></i>
                </button>
                <UiButton @click="login"><i class="fas fa-sign-in"></i> {{ t("Login") }}</UiButton>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { ColorThemeName, getTheme, setTheme } from "@/control/app-preferences";
import { AuthController } from "@/control/auth";
import { useAppEventListener } from "@/composables/useAppEventListener";
import { useAuthNavigation } from "@/composables/useAuthNavigation";
import UiButton from "@/components/utils/UiButton.vue";

defineOptions({ name: "TopBar" });

const emit = defineEmits<{
    "toggle-menu": [];
    openModal: [name: string];
}>();

const router = useRouter();
const { requireLogin } = useAuthNavigation();

const platformName = import.meta.env.VITE__PLATFORM_NAME || "Platform";
const loggedIn = ref(AuthController.isAuthenticated());
const theme = ref(getTheme());
const hasNotifications = ref(false);

const openAccountSettings = () => {
    router.push({ name: "account-settings" });
};

const openNotifications = () => {
    emit("openModal", "notifications");
};

const openUserSettings = () => {
    emit("openModal", "user-settings");
};

const selectLanguage = () => {
    emit("openModal", "change-language-modal");
};

const invertTheme = () => {
    setTheme(theme.value === "dark" ? "light" : "dark");
};

const login = () => {
    requireLogin();
};

const clickMenu = () => {
    emit("toggle-menu");
};

const onAuthChanged = () => {
    loggedIn.value = AuthController.isAuthenticated();
};

const onNotificationsChanged = (hasUnread: boolean) => {
    hasNotifications.value = hasUnread;
};

const onThemeChanged = (newTheme: ColorThemeName) => {
    theme.value = newTheme;
};

useAppEventListener("auth-status-changed", onAuthChanged);
useAppEventListener("theme-changed", onThemeChanged);
useAppEventListener("notifications-changed", onNotificationsChanged);
</script>

<style></style>
